import { CarouselProps } from '@engyalo/delivery-ui-components/lib/ui/components/Carousel'
import { HeaderListTypes } from '@engyalo/delivery-ui-components/lib/ui/components/HeaderList/HeaderList.types'
import { DefaultProps } from '../../../interfaces'
import {
  TemplateActionNames,
  TextLibrary,
  CinnamonActionNames,
  CinnamonPaths,
  Referrers,
} from '../defaultConstants'
import {
  ItemCardProductRecommendedCarouselPrefilledQuantity,
  ItemCardProductRecommendedCarousel,
  buildRecommendationCarouselItemCard,
} from './ItemCard'

export const CarouselBanners: DefaultProps<CarouselProps> = {
  hideOnScroll: true,
  images: '{{banners.banners}}' as any,
  imageActions: {
    cinnamonActions: [
      {
        name: TemplateActionNames.CATALOG_BANNERS_CLICK,
        args: {
          data: '{{this.data}}',
          navigateToCategory: '{{config.options.usePaginatedCategories}}',
        },
      },
    ],
  } as any,
  slidesPerView: 1,
  image: { size: '100%', ratio: 50, cover: true, radius: 4 } as any,
  'data-testid': 'banners',
  styles: {
    margin: '0 8px',
    width: 'auto',
    '.swiper-pagination': {
      position: 'sticky',
    },
  },
  delayTimeAutoPlay: 8000,
}

export const CarouselRecomendedProducts: DefaultProps<CarouselProps> = {
  items: '{{state.suggestedProductsSlice.suggestedProducts}}' as any,
  itemCardOrientation: 'horizontal',
  slidesPerView: 1.08,
  spaceBetweenSlides: 8,
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.SUGGESTED_ITEMS_FETCH_PRODUCTS,
        args: {
          storefrontName: '{{state.defaultSlice.storeName}}',
          sessionUid: '{{state.defaultSlice.sessionId}}',
          limit:
            '{{env.REACT_APP_RECOMMENDED_ITEMS_QUANTITY}} || {{config.globals.recommendedProducts.widgetHome}}',
        },
      },
    ],
  },
  itemCard: ItemCardProductRecommendedCarousel,
  styles: {
    margin: '4px',
    width: 'auto',
    marginRight: '-5px',
    '.swiper-pagination': {
      position: 'sticky',
    },
    '@media(max-width: 374px)': {
      padding: 0,
    },
    '.swiper-slide img': {
      objectFit: 'contain',
    },
  },
}

export const CarouselRecomendedProductsWithSideCounter: DefaultProps<CarouselProps> =
  {
    items: '{{state.suggestedProductsSlice.suggestedProducts}}' as any,
    itemCardOrientation: 'horizontal',
    slidesPerView: 1.08,
    spaceBetweenSlides: 8,
    runOnLoad: {
      cinnamonActions: [
        {
          name: TemplateActionNames.SUGGESTED_ITEMS_FETCH_PRODUCTS,
          args: {
            storefrontName: '{{state.defaultSlice.storeName}}',
            sessionUid: '{{state.defaultSlice.sessionId}}',
            limit:
              '{{env.REACT_APP_RECOMMENDED_ITEMS_QUANTITY}} || {{config.globals.recommendedProducts.widgetHome}}',
          },
        },
      ],
    },
    itemCard: ItemCardProductRecommendedCarouselPrefilledQuantity,
    styles: {
      margin: '4px',
      width: 'auto',
      marginRight: '-5px',
      '.swiper-pagination': {
        position: 'sticky',
      },
      '@media(max-width: 374px)': {
        padding: 0,
      },
    },
  }

export const CarouselRecomendedProductsHead: DefaultProps<HeaderListTypes> = {
  label: TextLibrary.RECOMMENDED_PRODUCTS_HEADER_TITLE,
  emoji: '🔥',
  iconLabelPosition: 'after',
  buttonHeader: {
    label: TextLibrary.RECOMMENDED_PRODUCTS_HEADER_BUTTON,
    onClick: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.PUSH_VIEW,
          args: {
            path: `${CinnamonPaths.HOME}/recommended-products`,
            category: '{{catalog.categoryList}}',
          },
        },
      ],
    } as any,
  },
  hidden: '{{state.suggestedProductsSlice.hideCarousel}}' as any,
  styles: {
    marginLeft: '4px',
    '.MuiTypography-root': {
      fontWeight: 600,
    },
  },
}

/**
 * Where the productized recommendation placements get their `limit`.
 *
 * A binding rather than a number so a storefront can retune it from SCS under
 * `globals.recommendedProducts`, instead of redeclaring the whole
 * `runOnLoad.cinnamonActions` block per placement — `deepMerge` replaces
 * arrays outright, so there was no terser override before this.
 *
 * Deliberately no *literal* fallback (`|| 15`, `?? 15`). The operator itself
 * is fine between two bindings, as the suggested-products placements above
 * use it; the trap is a bare literal operand. `composeResult` returns one as
 * the raw string it split out of the expression (see
 * `utils/processData.ts`, pinned by `test12` in `utils/tests/processData.test.ts`,
 * which expects the string `'None'`), so `'{{...}} || 15'` yields `"15"`
 * **whenever the left side resolves undefined** — which was the whole fleet
 * before this default shipped. `limit` reaches the GraphQL `$limit: Int` with
 * no coercion on the way, so a string there fails the query outright.
 *
 * The default lives in `defaultGlobals` instead, which `processConfig`
 * deep-merges under every storefront config, and `processConfig` normalises
 * the value on the way through. So this resolves to a usable number unless
 * the pipeline itself regresses — see `utils/recommendationLimits.ts`.
 */
const RECOMMENDATION_CAROUSEL_LIMIT_BINDING =
  '{{config.globals.recommendedProducts.carouselLimit}}'

const recommendationCarouselBaseStyles = {
  margin: '4px',
  width: 'auto',
  marginRight: '-5px',
  '.swiper-pagination': {
    position: 'sticky',
  },
  '@media(max-width: 374px)': {
    padding: 0,
  },
  '.swiper-slide img': {
    objectFit: 'contain',
  },
}

const recommendationHeaderBaseStyles = {
  marginLeft: '4px',
  '.MuiTypography-root': {
    fontWeight: 600,
  },
}

/**
 * Generic carousel default props for the productized recommendation carousels
 * (`buyAgain`, `previouslyPurchased`, `recommendedForYou`). Tenants override
 * visual styling (background, border radius) and the `limit` arg via SCS
 * config; Cinnamon ships sensible generic defaults.
 *
 * `recommendedForYou` ships dark — the slice/hooks/defaultProps are wired in
 * code but no SCS config activates the carousel by default for any tenant.
 */
export const CarouselBuyAgain: DefaultProps<CarouselProps> = {
  items: '{{state.buyAgainSlice.items}}' as any,
  itemCardOrientation: 'horizontal',
  slidesPerView: 1.08,
  spaceBetweenSlides: 8,
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.BUY_AGAIN_FETCH_PRODUCTS,
        args: {
          storefrontName: '{{state.defaultSlice.storeName}}',
          sessionUid: '{{state.defaultSlice.sessionId}}',
          limit: RECOMMENDATION_CAROUSEL_LIMIT_BINDING,
        },
      },
    ],
  },
  itemCard: buildRecommendationCarouselItemCard(Referrers.BUY_AGAIN),
  styles: recommendationCarouselBaseStyles,
}

export const CarouselBuyAgainHead: DefaultProps<HeaderListTypes> = {
  label: TextLibrary.BUY_AGAIN_HEADER_TITLE,
  iconLabelPosition: 'after',
  buttonHeader: {
    label: TextLibrary.BUY_AGAIN_HEADER_BUTTON,
    onClick: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.PUSH_VIEW,
          args: {
            path: `${CinnamonPaths.HOME}/buy-again`,
            category: '{{catalog.categoryList}}',
          },
        },
      ],
    } as any,
  },
  hidden: '{{state.buyAgainSlice.hideCarousel}}' as any,
  styles: recommendationHeaderBaseStyles,
}

export const CarouselPreviouslyPurchased: DefaultProps<CarouselProps> = {
  items: '{{state.previouslyPurchasedSlice.items}}' as any,
  itemCardOrientation: 'horizontal',
  slidesPerView: 1.08,
  spaceBetweenSlides: 8,
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.PREVIOUSLY_PURCHASED_FETCH_PRODUCTS,
        args: {
          storefrontName: '{{state.defaultSlice.storeName}}',
          sessionUid: '{{state.defaultSlice.sessionId}}',
          limit: RECOMMENDATION_CAROUSEL_LIMIT_BINDING,
        },
      },
    ],
  },
  itemCard: buildRecommendationCarouselItemCard(Referrers.PREVIOUSLY_PURCHASED),
  styles: recommendationCarouselBaseStyles,
}

export const CarouselPreviouslyPurchasedHead: DefaultProps<HeaderListTypes> = {
  label: TextLibrary.PREVIOUSLY_PURCHASED_HEADER_TITLE,
  iconLabelPosition: 'after',
  buttonHeader: {
    label: TextLibrary.PREVIOUSLY_PURCHASED_HEADER_BUTTON,
    onClick: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.PUSH_VIEW,
          args: {
            path: `${CinnamonPaths.HOME}/previously-purchased`,
            category: '{{catalog.categoryList}}',
          },
        },
      ],
    } as any,
  },
  hidden: '{{state.previouslyPurchasedSlice.hideCarousel}}' as any,
  styles: recommendationHeaderBaseStyles,
}

export const CarouselRecommendedForYou: DefaultProps<CarouselProps> = {
  items: '{{state.recommendedForYouSlice.items}}' as any,
  itemCardOrientation: 'horizontal',
  slidesPerView: 1.08,
  spaceBetweenSlides: 8,
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.RECOMMENDED_FOR_YOU_FETCH_PRODUCTS,
        args: {
          storefrontName: '{{state.defaultSlice.storeName}}',
          sessionUid: '{{state.defaultSlice.sessionId}}',
          limit: RECOMMENDATION_CAROUSEL_LIMIT_BINDING,
        },
      },
    ],
  },
  itemCard: buildRecommendationCarouselItemCard(Referrers.RECOMMENDED_FOR_YOU),
  styles: recommendationCarouselBaseStyles,
}

export const CarouselRecommendedForYouHead: DefaultProps<HeaderListTypes> = {
  label: TextLibrary.RECOMMENDED_FOR_YOU_HEADER_TITLE,
  iconLabelPosition: 'after',
  buttonHeader: {
    label: TextLibrary.RECOMMENDED_FOR_YOU_HEADER_BUTTON,
    onClick: {
      cinnamonActions: [
        {
          name: CinnamonActionNames.PUSH_VIEW,
          args: {
            path: `${CinnamonPaths.HOME}/recommended-for-you`,
            category: '{{catalog.categoryList}}',
          },
        },
      ],
    } as any,
  },
  hidden: '{{state.recommendedForYouSlice.hideCarousel}}' as any,
  styles: recommendationHeaderBaseStyles,
}

/**
 * Carousel default props for the "Opciones similares" (alternative products)
 * carousel shown on the product-detail page. Unlike the home recommendation
 * carousels this one is SKU-keyed — it renders the alternatives for the
 * currently displayed product, two ItemCards per viewport (`slidesPerView: 2`).
 * The hook sorts the response by price ascending so the cheapest option shows
 * first (leftmost in LTR). There is no "Ver todos" page, so the header carries
 * no `buttonHeader`.
 *
 * NOTE: there is deliberately no `runOnLoad` here. A one-shot `runOnLoad` bound to
 * `{{params.sku}}` fires before `RouterParams` populates the route params, so it
 * can't supply the displayed sku (see COM-431). The fetch is triggered in code
 * from the PDP template via `useAlternativeProductsPdpTrigger`; this component
 * only binds the slice items for render.
 *
 * Not yet enabled by any SCS config as of COM-422 (ships dark) — the
 * query/slice/hook/defaultProps are wired in code; see the ticket for rollout
 * status. This defaultProps block is the canonical home for that note, since
 * activation is a config concern (the query/hook/slice layers behave the same
 * dark vs. live).
 */
export const CarouselAlternativeProducts: DefaultProps<CarouselProps> = {
  items: '{{state.alternativeProductsSlice.items}}' as any,
  itemCardOrientation: 'horizontal',
  slidesPerView: 2,
  spaceBetweenSlides: 8,
  itemCard: buildRecommendationCarouselItemCard(Referrers.ALTERNATIVE_PRODUCTS),
  styles: recommendationCarouselBaseStyles,
}

export const CarouselAlternativeProductsHead: DefaultProps<HeaderListTypes> = {
  label: TextLibrary.ALTERNATIVE_PRODUCTS_HEADER_TITLE,
  iconLabelPosition: 'after',
  hidden: '{{state.alternativeProductsSlice.hideCarousel}}' as any,
  styles: recommendationHeaderBaseStyles,
}
